/* Keep native Yotpo reviews and ratings authoritative. Load full reviews and Q&A near the viewport or on review intent; native top
 * stars stay eager. Never infer zero reviews from filtering or a failed request. */
(() => {
  if (window.partifyProductFeedbackV1) {
    window.partifyProductFeedbackV1.scan(document);
    return;
  }

  const counts = new Map();
  const failedScripts = new Set();
  const instances = new Map();
  const pendingWidgets = new Set();
  let initTimer = 0;
  let initStarted = 0;
  let initializing = false;

  // Use Yotpo's additive compatibility entrypoint when available: it initializes
  // new native widgets without refreshing the already rendered main reviews.
  // Batch insertions and wait for the async loader instead of adding a loader.
  function queueWidgets(node) {
    if (node) pendingWidgets.add(node);
    if (initTimer || initializing) return;
    initStarted = Date.now();
    initTimer = setTimeout(initializeWidgets, 50);
  }

  function initializeWidgets() {
    initTimer = 0;
    for (const node of pendingWidgets) {
      if (!node.isConnected) pendingWidgets.delete(node);
    }
    if (!pendingWidgets.size) return;
    const container = window.yotpoWidgetsContainer;
    if (typeof container?.initWidgets !== 'function') {
      if (Date.now() - initStarted < 30000) initTimer = setTimeout(initializeWidgets, 250);
      return;
    }
    const initializer = typeof window.yotpo?.initWidgets === 'function' ? window.yotpo : container;
    const batch = [...pendingWidgets];
    initializing = true;
    try {
      // Match the compatibility wrapper's additive flag even before it loads.
      // A default modern refresh would reset active review filters/pagination.
      Promise.resolve(initializer === container ? container.initWidgets(false) : initializer.initWidgets()).then(() => {
        batch.forEach(node => pendingWidgets.delete(node));
      }).catch(() => {
        // A temporarily unavailable loader is retried within the same window.
      }).finally(() => {
        initializing = false;
        if (pendingWidgets.size && Date.now() - initStarted < 30000) {
          initTimer = setTimeout(initializeWidgets, 250);
        }
      });
    } catch (_) {
      initializing = false;
      if (Date.now() - initStarted < 30000) initTimer = setTimeout(initializeWidgets, 250);
    }
  }

  function storeKey() {
    for (const script of document.scripts) {
      try {
        const url = new URL(script.src);
        if (url.hostname === 'cdn-widgetsrepository.yotpo.com') {
          const match = url.pathname.match(/^\/v1\/loader\/([A-Za-z0-9]+)$/);
          if (match) return match[1];
        }
      } catch (_) { /* Inline script. */ }
    }
    return null;
  }

  function confirmedCount(productId, questions = false) {
    const key = storeKey();
    if (!key || !/^\d+$/.test(productId || '')) return Promise.resolve(null);
    const cacheKey = key + ':' + productId + (questions ? ':questions' : '');
    if (!counts.has(cacheKey)) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      const url = questions ? 'https://api-cdn.yotpo.com/v1/widget/' + encodeURIComponent(key) + '/products/' + encodeURIComponent(productId) + '/questions?page=1&per_page=1' : 'https://api-cdn.yotpo.com/v3/storefront/store/' +
        encodeURIComponent(key) + '/product/' + encodeURIComponent(productId) +
        '/reviews?page=1&perPage=1';
      counts.set(cacheKey, fetch(url, {
        credentials: 'omit',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      }).then(response => {
        if (!response.ok) return null;
        return response.json();
      }).then(body => {
        const count = questions ? body?.response?.pagination?.total?.questions : body?.bottomline?.totalReview;
        return Number.isInteger(count) && count >= 0 ? count : null;
      }).catch(() => null).finally(() => clearTimeout(timeout)));
    }
    return counts.get(cacheKey);
  }

  function mountTemplate(container, name) {
    const template = container?.querySelector('template[data-partify-feedback-template="' + name + '"]');
    if (!template) return false;
    template.replaceWith(template.content.cloneNode(true));
    queueWidgets(container);
    return true;
  }

  function initialize(root) {
    if (instances.has(root)) return;
    const reviews = root.querySelector('[data-partify-reviews]');
    const fallback = root.querySelector('[data-partify-review-fallback]');
    const questions = root.querySelector('[data-partify-questions]');
    root.dataset.partifyFeedbackReady = 'true';
    let hasReviews = false, checked = false, total = null, questionsChecked = false, questionTotal = null, frame = 0;
    let destroyed = false, proximity = null, pendingStar = null, starStatus = null, starTimer = 0, intentVersion = 0;

    // Keep each original native seed separate from the app's SEO siblings. A
    // retry replaces only the failed widget; ready reviews are never refreshed.
    function controller(container, name, label) {
      const template = container?.querySelector('template[data-partify-feedback-template="' + name + '"]');
      const existing = !template && container?.querySelector('.yotpo-widget-instance');
      let seed = template?.content.cloneNode(true);
      if (!seed && existing) {
        seed = document.createDocumentFragment();
        const original = existing.cloneNode(false);
        original.removeAttribute('data-yotpo-element-loaded');
        seed.append(original);
      }
      let host = null, status = null, timer = 0, deadline = 0, attempts = 0, ready = false;
      function notice(message, retry, reload = false) {
        if (!status) {
          status = document.createElement('div');
          status.dataset.partifyFeedbackStatus = name;
          status.setAttribute('role', 'status');
          container.prepend(status);
        }
        status.replaceChildren();
        const text = document.createElement('p');
        text.textContent = message;
        status.append(text);
        if (retry && attempts < 4) {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = 'Retry ' + label.toLowerCase();
          button.addEventListener('click', () => start(true), { once: true });
          status.append(button);
        }
        if (reload) {
          const refresh = document.createElement('button');
          refresh.type = 'button'; refresh.textContent = 'Refresh page';
          refresh.addEventListener('click', () => location.reload());
          status.append(refresh);
        }
      }
      function start(retry = false) {
        if (destroyed || !container || ready || (attempts && !retry)) return;
        if (!seed) { queueWidgets(container); return; }
        if (attempts >= 4) return;
        attempts += 1;
        clearTimeout(timer); clearTimeout(deadline);
        if (!host && (template || retry)) {
          host = document.createElement('div');
          host.dataset.partifyNativeHost = name;
          (template || existing).replaceWith(host);
        }
        // A claimed/eager widget is left intact on the initial attempt.
        if (host) host.replaceChildren(seed.cloneNode(true));
        container.dataset.partifyWidgetState = 'loading';
        container.setAttribute('aria-busy', 'true');
        notice('Loading ' + label.toLowerCase() + '…', false);
        if (retry && name === 'reviews' && total === null) {
          checked = false;
          const key = storeKey();
          if (key) counts.delete(key + ':' + root.dataset.productId);
        }
        if (retry && name === 'questions' && questionTotal === null) {
          questionsChecked = false;
          const key = storeKey();
          if (key) counts.delete(key + ':' + root.dataset.productId + ':questions');
        }
        queueWidgets(container);
        timer = setTimeout(() => {
          if (ready || destroyed) return;
          container.dataset.partifyWidgetState = 'delayed';
          notice(label + ' are taking a little longer to load…', false);
        }, 8000);
        deadline = setTimeout(() => {
          if (ready || destroyed) return;
          container.dataset.partifyWidgetState = 'error';
          container.removeAttribute('aria-busy');
          const scriptFailed = failedScripts.has(name) || failedScripts.has('all');
          notice(scriptFailed || attempts >= 4 ? 'We couldn’t load ' + label.toLowerCase() + '. Please refresh the page to try again.' : 'We couldn’t load ' + label.toLowerCase() + '. Please try again.', !scriptFailed, true);
        }, 20000);
      }
      function complete() {
        if (ready) return;
        ready = true;
        clearTimeout(timer); clearTimeout(deadline);
        status?.remove();
        container.querySelector('[data-partify-feedback-placeholder]')?.remove();
        container.removeAttribute('data-partify-questions-pending');
        container.removeAttribute('data-partify-reviews-pending');
        container.removeAttribute('aria-busy');
        container.dataset.partifyWidgetState = 'ready';
      }
      function destroy() { clearTimeout(timer); clearTimeout(deadline); pendingWidgets.delete(container); }
      return { start, complete, destroy, isReady: () => ready, canRetry: () => attempts < 4 };
    }

    const main = controller(reviews, 'reviews', 'Customer reviews');
    const qna = controller(questions, 'questions', 'Customer questions');
    function mount() {
      if (destroyed || !root.isConnected) return;
      main.start(); qna.start(); proximity?.disconnect();
    }
    function interest(star) {
      if (star && !main.isReady()) {
        pendingStar = star; intentVersion += 1;
        if (!starStatus) {
          starStatus = document.createElement('span');
          starStatus.dataset.partifyStarStatus = '';
          starStatus.setAttribute('role', 'status');
          star.after(starStatus);
        }
        starStatus.textContent = 'Loading customer reviews…';
        clearTimeout(starTimer);
        starTimer = setTimeout(() => {
          if (!pendingStar || main.isReady() || destroyed) return;
          pendingStar = null;
          const retryable = main.canRetry() && !failedScripts.has('reviews') && !failedScripts.has('all');
          starStatus.textContent = retryable ? 'Reviews are taking longer to load. ' : 'Please refresh the page to try loading reviews again.';
          if (!retryable) {
            const refresh = document.createElement('button'); refresh.type = 'button'; refresh.textContent = 'Refresh page';
            refresh.addEventListener('click', () => location.reload()); starStatus.append(refresh); return;
          }
          const retry = document.createElement('button');
          retry.type = 'button'; retry.textContent = 'Try again';
          retry.addEventListener('click', () => { main.start(true); interest(star); }, { once: true });
          starStatus.append(retry);
        }, 20000);
      }
      mount();
    }
    function cancelIntent(event) {
      if (starStatus?.contains(event?.target)) return;
      pendingStar = null; intentVersion += 1; clearTimeout(starTimer); starStatus?.remove(); starStatus = null;
    }

    function synchronize() {
      frame = 0;
      if (destroyed || !root.isConnected) return;
      const nativeQuestions = questions?.querySelector('#yotpo-questions-and-answers');
      if (nativeQuestions?.querySelector('.yotpo-question') || (questionTotal === 0 && nativeQuestions?.querySelector('.yotpo-header-container'))) qna.complete();
      if (!qna.isReady() && nativeQuestions?.querySelector('.yotpo-header-container') && !questionsChecked) {
        questionsChecked = true;
        confirmedCount(root.dataset.productId, true).then(count => { questionTotal = count; synchronize(); });
      }
      if (!reviews) return;
      const native = reviews.querySelector('#yotpo-reviews-main-widget');
      const score = native?.querySelector('.yotpo-bottom-line[average-score]');
      const nativeReview = native?.querySelector('.yotpo-review');
      if (nativeReview || Number(score?.getAttribute('average-score')) > 0) hasReviews = true;
      const empty = native?.querySelector('.yotpo-empty-state');
      const confirmedEmpty = !hasReviews && !!empty && total === 0;
      if (nativeReview || confirmedEmpty) {
        main.complete();
        if (pendingStar) {
          const button = pendingStar;
          const version = intentVersion;
          pendingStar = null; clearTimeout(starTimer); starStatus?.remove(); starStatus = null;
          // Yotpo attaches its summary-modal click handler when main reviews
          // initialize. Replay an early click through that native handler.
          if (!document.querySelector('.yotpo-summary-footer-read-all-reviews')) {
            // The native summary integration may replace the initial star button.
            setTimeout(() => {
              if (destroyed || intentVersion !== version) return;
              const current = button.isConnected ? button : document.querySelector('.product--outer .yotpo-sr-bottom-line-summary');
              if (!document.querySelector('.yotpo-summary-footer-read-all-reviews')) current?.click();
            }, 250);
          }
        }
      }
      if (fallback) {
        if (confirmedEmpty) mountTemplate(fallback, 'fallback');
        const card = fallback.querySelector('.glide__slide:not(.glide__slide--clone) .reviews-carousel-card');
        const show = confirmedEmpty && !!card;
        if (fallback.hidden === show) {
          fallback.hidden = !show;
          if (show) requestAnimationFrame(() => { if (!destroyed && root.isConnected) window.dispatchEvent(new Event('resize')); });
        }
        root.dataset.partifyReviewState = hasReviews || total > 0 ? 'populated' : show ? 'empty' : 'loading';
      }
      if (!hasReviews && empty && !checked) {
        const productId = reviews.querySelector('[data-yotpo-product-id]')?.getAttribute('data-yotpo-product-id');
        if (!productId || productId !== root.dataset.productId || !storeKey()) return;
        checked = true;
        confirmedCount(productId).then(count => { total = count; synchronize(); });
      }
    }

    const observer = new MutationObserver(() => { if (!frame) frame = requestAnimationFrame(synchronize); });
    if (reviews) observer.observe(reviews, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['average-score'] });
    if (fallback) observer.observe(fallback, { childList: true, subtree: true });
    if (questions) observer.observe(questions, { childList: true, subtree: true });
    if (root.querySelector('template[data-partify-feedback-template="reviews"], template[data-partify-feedback-template="questions"]')) {
      if ('IntersectionObserver' in window && !window.Shopify?.designMode) {
        proximity = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) mount(); }, { rootMargin: '1000px 0px', threshold: 0 });
        proximity.observe(questions || reviews);
      } else mount();
    }
    function destroy() {
      destroyed = true; cancelIntent();
      observer.disconnect(); proximity?.disconnect();
      if (frame) cancelAnimationFrame(frame);
      main.destroy(); qna.destroy(); pendingWidgets.delete(fallback);
      delete root.dataset.partifyFeedbackReady;
      instances.delete(root);
    }
    instances.set(root, { mount, interest, cancelIntent, destroy });
    synchronize();
  }

  function scan(scope) {
    if (scope.matches?.('[data-partify-product-feedback]')) initialize(scope);
    scope.querySelectorAll('[data-partify-product-feedback]').forEach(initialize);
  }
  function primaryStar(target) {
    return target?.closest('.product--outer .yotpo-sr-bottom-line-summary');
  }
  function warm(event) {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (primaryStar(target)) instances.forEach(instance => instance.mount());
  }
  document.addEventListener('pointerover', warm, { capture: true, passive: true });
  document.addEventListener('focusin', warm, true);
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const star = primaryStar(target);
    if (star) instances.forEach(instance => instance.interest(star));
    else if (target?.closest('.yotpo-summary-footer-read-all-reviews, a[href*="#yotpo"], a[href="#reviews"]')) instances.forEach(instance => instance.mount());
  }, true);
  ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(name => document.addEventListener(name, event => instances.forEach(instance => instance.cancelIntent(event)), { capture: true, passive: true }));
  window.addEventListener('hashchange', () => { if (/yotpo|reviews|questions/i.test(location.hash)) instances.forEach(instance => instance.mount()); });
  document.addEventListener('error', event => {
    const script = event.target;
    if (script?.tagName !== 'SCRIPT') return;
    try {
      const url = new URL(script.src);
      if (url.hostname !== 'cdn-widgetsrepository.yotpo.com') return;
      if (url.pathname.includes('widget-reviews-main-widget/')) failedScripts.add('reviews');
      else if (url.pathname.includes('widget-questions-and-answers/')) failedScripts.add('questions');
      else if (/\/v1\/loader\/|widgets-initializer\//.test(url.pathname)) failedScripts.add('all');
    } catch (_) { /* Not a native external script. */ }
  }, true);
  document.addEventListener('load', event => { if (event.target?.tagName === 'SCRIPT' && /yotpo\.com/.test(event.target.src || '') && pendingWidgets.size) queueWidgets(); }, true);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => { instances.forEach((instance, root) => { if (event.target === root || event.target.contains(root)) instance.destroy(); }); });
  window.partifyProductFeedbackV1 = { scan };
  scan(document);
  if (/yotpo|reviews|questions/i.test(location.hash)) instances.forEach(instance => instance.mount());
})();
